"use client";
import React, { useCallback, useMemo, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from 'react-redux';
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Button, Col, Form, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { saveProductMapping } from "../../store/slices/saveProductMapping";
import { getConfig } from '../../store/slices/getConfig';

//  Updated initial field
const initialField = { assetsfamily: "", assetsValue: "" };

//  Updated validation schema
// const validationSchema = Yup.object({
//   mappings: Yup.array().of(
//     Yup.object().shape({
//       assetsfamily: Yup.string().required("assetsfamily is required"),
//       assetsValue: Yup.string().when("assetsfamily", {
//         is: (val) => !!val,
//         then: (schema) => schema.required("assetsValue is required"),
//         otherwise: (schema) => schema.notRequired(),
//       }),
//     })
//   ),
// });


const validateMappings = (values) => {
  const errors = {};

  const mappings = values.mappings || [];

  // No rows → valid
  if (mappings.length === 0) {
    return errors;
  }

  // Exactly one row
  if (mappings.length === 1) {
    const { assetsfamily, assetsValue } = mappings[0];

    // BOTH EMPTY → VALID
    if (!assetsfamily && !assetsValue) {
      return errors;
    }

    // One is filled, so both are required
    if (!assetsfamily || !assetsValue) {
      errors.mappings = [{}];

      if (!assetsfamily) {
        errors.mappings[0].assetsfamily =
          "assetsfamily is required";
      }

      if (!assetsValue) {
        errors.mappings[0].assetsValue =
          "assetsValue is required";
      }

      return errors;
    }

    return errors;
  }

  // Multiple rows
  const mappingErrors = [];

  mappings.forEach((mapping, index) => {
    const rowErrors = {};

    if (!mapping.assetsfamily) {
      rowErrors.assetsfamily = "assetsfamily is required";
    }

    if (!mapping.assetsValue) {
      rowErrors.assetsValue = "assetsValue is required";
    }

    mappingErrors[index] = rowErrors;
  });

  // Only add mappings errors if there is actually an error
  if (
    mappingErrors.some(
      (row) => Object.keys(row).length > 0
    )
  ) {
    errors.mappings = mappingErrors;
  }

  return errors;
};


function transformAssetFamilies(data = []) {
  return (data || []).reduce(
    (result, family) => {
      result.parentList.push({
        code: family.code,
        label: family.labels,
      });

      result.attributeMap[family.code] = (family.assetAttributes || []).map(
        (attr) => ({
          code: attr.code,
          label: attr.label,
        })
      );

      return result;
    },
    {
      parentList: [],
      attributeMap: {},
    }
  );
}


function assetsFamilies({ toggle, setToggle, showToast }) {
  const dispatch = useDispatch();
  const assetsData = useSelector(state => state.assetFamilies);
  const { parentList, attributeMap } = transformAssetFamilies(assetsData.data);
  const savedData = useSelector((state) => state.config.data);
  const configId = useSelector((state) => state.saveCreds.configId);
  const storeDomain = useSelector(state => state.config?.clientStoreHash);

  const formik = useFormik({
    initialValues: {
      mappings: [initialField],
    },
    validate: validateMappings,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async(values) => {

      const payload = values.mappings.reduce((acc, item) => {
        acc[item.assetsfamily] = item.assetsValue;
        return acc;
      }, {});

      const req = {
        attributeMapping: {
        ...(savedData?.attributeMapping || {}),
        "assetCollection":payload
        },
        struConfigId: configId || savedData?.struConfigId
      }
        await dispatch(saveProductMapping(req)).unwrap();
        await dispatch(getConfig(storeDomain)).unwrap();
        showToast("saveattributeMapping");
      setToggle(false);
    },
  });

  useEffect(() => {

    const assetCollection = savedData?.attributeMapping?.assetCollection;

    // if (!assetCollection) return;
    if (!assetCollection || Object.keys(assetCollection).length === 0){
      formik.setFieldValue("mappings", [initialField]);
      return;
    }

    const mappings = Object.entries(assetCollection).map(
      ([assetsfamily, assetsValue]) => ({
        assetsfamily,
        assetsValue,
      })
    );

    formik.setFieldValue("mappings", mappings);
  }, [savedData?.attributeMapping?.assetCollection]);

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  useEffect(() => {
  values.mappings.forEach((mapping, index) => {
    if (!mapping.assetsfamily && mapping.assetsValue) {
      formik.setFieldValue(
        `mappings[${index}].assetsValue`,
        ""
      );
    }
  });
}, [values.mappings]);
  
  const handleAddFields = useCallback(() => {
    const updatedMappings = [...values.mappings, initialField];
    formik.setFieldValue("mappings", updatedMappings);
  }, [values.mappings, formik]);

  const handleRemoveFields = useCallback(
    (index) => {
      const updatedMappings = values.mappings.filter((_, i) => i !== index);
      formik.setFieldValue("mappings", updatedMappings);
    },
    [values.mappings, formik]
  );

  // Memoize the last index
  const lastIndex = useMemo(
    () => values.mappings.length - 1,
    [values.mappings.length]
  );

  const renderMultiAssetFamilies = useCallback(
    (options, currentIndex) => {
      if (!Array.isArray(options)) return [];

      // Asset families selected in all other rows
      const selectedFamilies = formik.values.mappings
        .filter((_, index) => index !== currentIndex)
        .map((item) => item.assetsfamily)
        .filter(Boolean);

      return options
        .filter((option) => !selectedFamilies.includes(option.code))
        .map((option) => ({
          code: option.code,
          label: option.label,
        }));
    },
    [formik.values.mappings]
  );

  return (
    <>
      <Modal
        show={toggle}
        onHide={() => setToggle(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        dialogClassName="custom-modal-width"
        backdrop="static"
        centered
      >
        <div className="bg-white p-6 rounded-lg shadow-lg p-5 rounded-3">
          {/* <h5 className="fw-bold mb-4 border-bottom border-gray">
            Edit Secondary assetsValues
          </h5> */}

          <Row className="mappingInput">
            <Col sm={5}>
              <p className="mb-4">
                <strong>Select the Asset Family</strong>
              </p>
            </Col>
            <Col sm={5} className="mx-5 pimAttribute">
              <p className="mb-4">
                <strong>Select the Asset Attribute</strong>
              </p>
            </Col>
          </Row>

          {values.mappings.map((inputField, index) => (
            <React.Fragment key={index}>
              <Row className="mb-3">
                {/* assetsfamily Dropdown */}
                <Col sm={5}>
                  <Form.Group controlId={`mappings.${index}.assetsfamily`}>
                    <Form.Select
                      name={`mappings[${index}].assetsfamily`}
                      value={inputField.assetsfamily}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.assetsfamily &&
                        touched.mappings?.[index]?.assetsfamily
                      }
                    >
                      <option value="">Select the Assets Family</option>
                      {renderMultiAssetFamilies(parentList, index).map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.assetsfamily}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Compare arrows icon */}
                <Col
                  sm={1}
                  className="d-flex justify-content-center compareIcon"
                >
                  <CompareArrowsIcon className="mt-2" />
                </Col>

                {/* assetsValue Dropdown */}
                <Col sm={5}>
                  <Form.Group controlId={`mappings.${index}.assetsValue`}>
                    <Form.Select
                      name={`mappings[${index}].assetsValue`}
                      value={inputField.assetsValue}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.mappings?.[index]?.assetsValue &&
                        touched.mappings?.[index]?.assetsValue
                      }
                    >
                    <option value="">Select the Asset Attribute</option>
                    {(attributeMap[inputField.assetsfamily] || []).map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.mappings?.[index]?.assetsValue}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Remove button */}
                <Col sm={1} className="ps-0 pt-1">
                  {values.mappings.length > 1 && (
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleRemoveFields(index)}
                      className="channelActionBtn p-0"
                    >
                      <DeleteOutlineIcon style={{ fontSize: "30px" }} />
                    </Button>
                  )}
                </Col>
              </Row>

              {/* Add button only on last row */}
              {index === lastIndex && (
                <Row>
                  <Col
                    sm={10}
                    className="d-flex justify-content-center mt-3 ms-4"
                  >
                    <Button
                      type="button"
                      onClick={handleAddFields}
                      style={{
                        border: "1px solid lightgray",
                        borderRadius: "7px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                      }}
                    >
                      Add new mapping
                    </Button>
                  </Col>
                </Row>
              )}
            </React.Fragment>
          ))}
        </div>

        <Modal.Footer>
          <button
            onClick={() => setToggle(false)}
            className="modal_cancel_button modal_button model_footer absolute top-2 right-2 text-gray-600 hover:text-black"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="modal_save_button modal_button model_footer absolute top-2 right-2 text-gray-600 hover:text-black"
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default assetsFamilies;
